import { useNavigate } from "react-router-dom";
import { BRAND } from "@/constants/branding";
import { useAuth } from "@/context/AuthContext";
import { LogOut, Store as StoreIcon, Home } from "lucide-react";
import { Button } from "@/components/ui/button";
import NotificationBell from "@/components/NotificationBell";

export default function DashboardShell({ title, subtitle, nav, active, onNavigate, children, accent = "primary", hideBell = false }) {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  return (
    <div className="min-h-screen bg-background flex flex-col lg:flex-row">
      <aside className="lg:w-64 bg-deep text-white/75 lg:min-h-screen flex lg:flex-col shrink-0">
        <div className="p-5 border-b border-white/10 flex items-center justify-between lg:block">
          <button onClick={() => navigate("/")} className="bg-white rounded-lg px-3 py-1.5 inline-block">
            <img src={BRAND.logo} alt="Zilkart" className="h-7 w-auto" />
          </button>
          <p className="hidden lg:block mt-3 text-xs uppercase tracking-wider text-white/45">{title}</p>
        </div>
        <nav className="flex lg:flex-col gap-1 p-3 overflow-x-auto lg:overflow-visible no-scrollbar flex-1">
          {nav.map((n) => (
            <button
              key={n.key}
              onClick={() => onNavigate(n.key)}
              className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${active === n.key ? "bg-primary text-white" : "hover:bg-white/10 text-white/75"}`}
              data-testid={`dash-nav-${n.key}`}
            >
              <n.icon className="h-4 w-4" /> {n.label}
              {n.badge > 0 && <span className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-[#e9c46a] text-deep text-[11px] font-bold flex items-center justify-center" data-testid={`dash-badge-${n.key}`}>{n.badge}</span>}
            </button>
          ))}
        </nav>
        <div className="p-3 border-t border-white/10 hidden lg:block space-y-1">
          <button onClick={() => navigate("/")} className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm hover:bg-white/10 transition-colors" data-testid="dash-storefront">
            <Home className="h-4 w-4" /> View Storefront
          </button>
          <button onClick={() => { logout(); navigate("/"); }} className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm text-red-400 hover:bg-white/10 transition-colors" data-testid="dash-logout">
            <LogOut className="h-4 w-4" /> Logout
          </button>
        </div>
      </aside>

      <div className="flex-1 min-w-0">
        <header className="bg-white border-b border-border px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between sticky top-0 z-10">
          <div>
            <h1 className="text-lg sm:text-xl font-bold font-heading">{subtitle}</h1>
            <p className="text-xs text-muted-foreground">{user?.name} · <span className="capitalize">{user?.role?.replace("_", " ")}</span></p>
          </div>
          <div className="flex items-center gap-2">
            {!hideBell && <NotificationBell onOpenLink={onNavigate} />}
          </div>
          <div className="flex items-center gap-2 lg:hidden ml-2">
            <Button variant="outline" size="sm" onClick={() => navigate("/")}><StoreIcon className="h-4 w-4" /></Button>
            <Button variant="outline" size="sm" onClick={() => { logout(); navigate("/"); }}><LogOut className="h-4 w-4" /></Button>
          </div>
        </header>
        <main className="p-4 sm:p-6 lg:p-8">{children}</main>
      </div>
    </div>
  );
}

export function StatCard({ label, value, icon: Icon }) {
  return (
    <div className="bg-white rounded-xl border border-border p-5">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{label}</p>
        {Icon && <Icon className="h-5 w-5 text-primary" />}
      </div>
      <p className="text-2xl font-bold mt-2">{value}</p>
    </div>
  );
}
